import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { SearchAdminDocument } from "../../http/types/documents";
import { useDocumentsApi } from "../../http/useDocuments";
import { PaginationQuery } from "../../types";
import { formatDate } from "../../utils";
import SvgDownload from "../icons/Download";
import DownloadLink from "../ui/DownloadLink";
import FileImage from "../ui/FileIcon";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import SearchBar from "../ui/SearchBar";
import Table from "../ui/Table";

const Documents: FC = () => {
  return (
    <SearchContextProvider>
      <DocumentsContent />
    </SearchContextProvider>
  );
};

const DocumentsContent: FC = () => {
  const i18n = useI18n();
  const api = useApi();
  const documentsApi = useDocumentsApi();

  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { actions, setActions } = useSearchContext();

  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.documents.search;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = documentsApi.useMyProjectDocuments(query, aborter.current.signal);

  useEffect(() => {
    setActions(
      <SearchBar
        placeholder={searchPlaceholder}
        value={filters.state.q ?? ""}
        onChange={(term) => {
          aborter.current.abort();
          aborter.current = new AbortController();
          filters.setSearchTerm(term);
        }}
      />,
    );

    return () => {
      setActions(null);
    };
  }, [filters.state.q, searchPlaceholder, setActions]);

  return (
    <>
      <Header
        title={i18n.translation.documents.plural}
        error={error}
        path={[{ display: i18n.translation.documents.all }]}
        buttons={actions}
      />

      <FlexContent padding>
        <Table<SearchAdminDocument>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left" style={{ width: 1 }}></th>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.projects.singular}</th>
              <th align="left">{i18n.translation.common.uploadedBy}</th>
              <th align="left">{i18n.translation.common.uploadedAt}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <Table.IconTd>
                <FileImage fileName={item.name} />
              </Table.IconTd>
              <td>
                <DownloadLink
                  url={`/files/project_documents/${item.id}/${encodeURIComponent(item.name)}`}
                  fileName={encodeURIComponent(item.name)}
                >
                  {item.name}
                </DownloadLink>
              </td>
              <td>{item.project.name}</td>
              <td>
                {item.uploader.firstName} {item.uploader.lastName}
              </td>
              <td>{formatDate(item.uploadedAt)}</td>
            </>
          )}
          rowActions={(item) => [
            {
              title: i18n.translation.common.download,
              onClick: () =>
                api
                  .downloadFile(
                    `/files/project_documents/${item.id}/${encodeURIComponent(item.name)}`,
                    encodeURIComponent(item.name),
                  )
                  .catch((err) => dispatch(showErrorNotification(i18n, err))),
              icon: SvgDownload,
            },
          ]}
          total={data?.total}
          error={!!error}
        />
      </FlexContent>
    </>
  );
};

export default Documents;
